import React from 'react';
import { Box, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { TipTapEditor } from '../../../TipTap';
import { FieldPreview } from './FieldBodyPreview';

/**
 * Classic bordered-card design layout: drag handle on top, card body, bottom
 * toolbar. Presentational only — state and the built control/settings elements
 * are owned by WysiwygFieldBlock.
 */
export default function ClassicFieldLayout({
  setNodeRef,
  style,
  attributes,
  listeners,
  isDragging,
  showControls,
  palette,
  theme,
  field,
  isDisplayField,
  availablePlaceholders,
  onFieldUpdate,
  controlButtons,
  settingsPanel,
  onMouseEnter,
  onMouseLeave,
}) {
  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        overflow: 'visible',
        cursor: 'default',
      }}
    >
      {/* Drag handle at top center */}
      <Box
        className="drag-handle"
        {...listeners}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: '1px',
          cursor: 'grab',
          color: palette.textSecondary,
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.15s ease',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon sx={{ width: 16, height: 12, transform: 'rotate(90deg)' }} />
      </Box>

      {/* Field card */}
      <Box
        sx={{
          border: `1px solid ${showControls ? palette.textSecondary : palette.border}`,
          borderRadius: '10px',
          backgroundColor: palette.surface,
          overflow: 'hidden',
          transition: 'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
          ...(showControls && { boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }),
        }}
      >
        {/* Main content */}
        <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {!isDisplayField && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0, fontSize: '12px', fontWeight: 600, color: palette.textPrimary }}>
                <TipTapEditor
                  value={field.label || ''}
                  onChange={(val) => onFieldUpdate({ label: val })}
                  showToolbar={false}
                  stripHtml={true}
                  availablePlaceholders={availablePlaceholders}
                  placeholder="Type your question..."
                  minHeight="auto"
                  className="tiptap-inline"
                />
              </Box>
              {field.required && (
                <Typography sx={{ fontSize: '11px', color: palette.errorText || '#b91c1c', flexShrink: 0 }}>*</Typography>
              )}
            </Box>
          )}

          <FieldPreview
            field={field}
            onUpdate={onFieldUpdate}
            palette={palette}
            theme={theme}
            availablePlaceholders={availablePlaceholders}
          />

          {!isDisplayField && field.helpText && (
            <Box sx={{ mt: 0.25 }}>
              <Typography sx={{ fontSize: '11px', color: palette.textSecondary }}>
                {field.helpText}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Inline settings panel */}
        {settingsPanel}
      </Box>

      {/* Bottom toolbar */}
      <Box
        className="block-bottom-bar"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          py: '2px',
          opacity: showControls ? 1 : 0,
          height: showControls ? '24px' : 0,
          overflow: 'hidden',
          transition: 'opacity 0.15s ease, height 0.15s ease',
        }}
      >
        {controlButtons}
      </Box>
    </Box>
  );
}
